import { useEffect, useState } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { CardHeader, CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import { Spacer } from 'foundations-components/components/Spacer';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { captureNetworkError } from 'growth-onboarding-reliability/utils/raven';
import HelpWidgetButton from '../button/HelpWidgetButton';
import TaskActionButton from '../button/TaskActionButton';
import TaskDuration from './TaskDuration';
import { TASK_FADE_DURATION_MS } from '../../../constants/task';
import { useTaskApi } from '../../../hooks/task';
import { useTracker } from '../../../hooks/tracking';
import { fadeIn, fadeOut } from '../../../utils/keyFrames';
import { FEATURE_SWITCH_APPS } from '../../FeatureSwitch';
import { FeatureSwitch } from '../../FeatureSwitch';
import TaskAssignmentLinks from './TaskAssignmentLinks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
/* @bend-expect no-styled-foundations-components */
const SkippedTaskCard = styled(CardWrapper).withConfig({
  displayName: "SkippedTask__SkippedTaskCard",
  componentId: "sc-68tj9b-0"
})(["animation:", " ", "ms ease-in-out;"], ({
  isTaskSkipped
}) => isTaskSkipped ? fadeIn : fadeOut, TASK_FADE_DURATION_MS);
const SkippedTask = ({
  durationInMinutes,
  expanded,
  onExpandedChange,
  task
}) => {
  const {
    checkAndCompleteTask,
    completeTask,
    undoSkipTask
  } = useTaskApi();
  const [isTaskSkipped, setIsTaskSkipped] = useState(true);
  const {
    trackTaskInteraction
  } = useTracker();
  const {
    key
  } = task;
  useEffect(() => {
    checkAndCompleteTask(key).catch(captureNetworkError);
  }, [checkAndCompleteTask, key]);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsxs(SkippedTaskCard, {
      compact: true,
      "data-onboarding": `skipped-task:${task.key}`,
      "data-task-status": "skipped",
      "data-test-id": `task:${key}`,
      expandable: true,
      expanded: isTaskSkipped && expanded,
      isTaskSkipped: isTaskSkipped,
      marginBlockEnd: "0",
      onExpandedChange: isExpanded => onExpandedChange(isExpanded, 'skipped'),
      children: [/*#__PURE__*/_jsxs(Flex, {
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsx(CardHeader, {
          "data-test-id": "task-header",
          flexGrow: 1,
          minInlineSize: "0",
          paddingBlockEnd: "0",
          paddingInline: "400",
          title: /*#__PURE__*/_jsxs(Flex, {
            alignItems: "stretch",
            flexDirection: "column",
            maxInlineSize: "100%",
            children: [/*#__PURE__*/_jsx(Text, {
              textWrap: "wrap",
              typography: "heading-100",
              children: task.name
            }), /*#__PURE__*/_jsx(TaskDuration, {
              duration: durationInMinutes
            })]
          }),
          titleSize: "small"
        }), /*#__PURE__*/_jsx(View, {
          marginBlockStart: "600",
          paddingInline: "400",
          children: /*#__PURE__*/_jsx(Text, {
            use: "help",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "setup-guide-components.checklist.skipped"
            })
          })
        })]
      }), /*#__PURE__*/_jsx(CardSection, {
        "data-test-id": "task-section",
        paddingBlockEnd: "0",
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "stretch",
          flexDirection: "column",
          gap: "400",
          paddingBlockEnd: "500",
          paddingInline: "400",
          children: [/*#__PURE__*/_jsxs(Text, {
            as: "span",
            children: [task.description && /*#__PURE__*/_jsxs(_Fragment, {
              children: [typeof task.description === 'string' ? task.description.trim() : task.description, /*#__PURE__*/_jsx(Spacer, {
                display: "inline-block",
                space: "100"
              })]
            }), /*#__PURE__*/_jsx(HelpWidgetButton, {
              task: task
            })]
          }), /*#__PURE__*/_jsxs(ButtonWrapper, {
            flexWrap: "nowrap",
            children: [/*#__PURE__*/_jsx(TaskActionButton, {
              checkAndCompleteTask: checkAndCompleteTask,
              completeTask: completeTask,
              task: task
            }), /*#__PURE__*/_jsx(FeatureSwitch, {
              allowedApps: [FEATURE_SWITCH_APPS.USER_GUIDE],
              skipFeatureLoad: !expanded,
              children: /*#__PURE__*/_jsx(TaskAssignmentLinks, {
                task: task
              })
            })]
          }), /*#__PURE__*/_jsx(Link, {
            onClick: () => {
              setIsTaskSkipped(false);
              trackTaskInteraction('click task undo skip', task.key);
              window.setTimeout(() => undoSkipTask(task.key), TASK_FADE_DURATION_MS);
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "setup-guide-components.checklist.undoSkip"
            })
          })]
        })
      })]
    })
  );
};
export default SkippedTask;